import type { View } from 'common/types/view';
import type { ViewColumn } from 'common/types/viewColumn';
import { filterToWhereClauseComponent } from 'common/visualizations/helpers/SoqlHelpers';
import { Vif } from 'common/visualizations/vif';
import _ from 'lodash';
import { migrateVif } from 'common/visualizations/helpers/migrateVif';

const LARGE_COUNT_THRESHOLD = 999999;

// This will only work with a vif from an AG-TABLE otherwise will return a *
const getSelectFromVif = (vif: Vif): string => {
  if (!vif?.series?.[0] || vif.series[0]?.type !== 'agTable') return '*';

  const visibleColumns = vif.series[0]?.dataSource.dimension?.columns || [];
  const columnClause = _.compact(
    visibleColumns.map((column: ViewColumn) =>
      !column.hide && !(column.flags || []).includes('hidden') ? column.fieldName : undefined
    )
  ).join(', ');
  return _.isEmpty(columnClause) ? '*' : columnClause;
};

export const getWhereFromVif = (vif: Vif) => {
  const filters = vif?.series?.[0]?.dataSource?.filters || [];
  // Filter out invalid filters that don't have the required properties
  const validFilters = filters.filter(
    (filter) => filter && typeof filter === 'object' && 'function' in filter
  );
  return _.compact(validFilters.map(filterToWhereClauseComponent)).join(' AND ');
};

const convertAscending = (ascending: boolean) => (ascending ? 'ASC' : 'DESC');

export const getOrderByFromVif = (vif: Vif) => {
  const order = (vif?.configuration?.order || []).filter((v) => v.columnName !== ':id');
  return order.length
    ? order.map((v) => `${v.columnName} ${convertAscending(!!v.ascending)}`).join(', ')
    : undefined;
};

const getSearchFromVif = (vif: Vif) =>
  // @ts-expect-error TS(2339) search string is not on the vif type but it's there
  vif?.series?.[0]?.dataSource?.searchString || undefined;

export const getQueryStringFromVif = (vif: Vif) => {
  const cleanVif = migrateVif(vif);
  const selectClause = getSelectFromVif(cleanVif);
  const whereClause = getWhereFromVif(cleanVif);
  const orderBy = getOrderByFromVif(cleanVif);
  const searchClause = getSearchFromVif(cleanVif);

  let queryString = `SELECT ${selectClause}`;
  if (whereClause) queryString += ` WHERE ${whereClause}`;
  if (orderBy) queryString += ` ORDER BY ${orderBy}`;
  if (searchClause) queryString += ` SEARCH "${searchClause}"`;
  return queryString;
};

const getNonSoqlOrderBy = (view: View) => {
  const order = view.metadata?.jsonQuery?.order;
  return !order || !order.length
    ? undefined
    : ` ORDER BY ${order
        .map((v: any) => `${v.columnFieldName} ${convertAscending(!!v.ascending)}`)
        .join(', ')}`;
};

// return a SELECT query of all the non-hidden columns on the view
// plus an order by if there is one stored in the jsonQuery object (default views only)
export const getQueryStringFromView = (view: View) => {
  if (!view.columns) return undefined;

  const queryString = view.columns
    .filter((v) => !v.flags?.includes('hidden'))
    .map((v) => v.fieldName)
    .join(', ');

  return `SELECT ${queryString}${getNonSoqlOrderBy(view) || ''}`;
};

export const shouldRenderWarning = (
  downloadType: string | undefined,
  selectedFiltered: boolean,
  rowCountFiltered: number | undefined,
  rowCountAll: number | undefined
) => {
  const count = selectedFiltered ? rowCountFiltered : rowCountAll;
  return downloadType === 'xlsx' && count !== undefined && count > 5000;
};

// A helper function to determine whether the selected download option exceeds threshold.
export const shouldRenderLargeDatasetWarning = (
  selectedFiltered: boolean,
  rowCountFiltered: number | undefined,
  rowCountAll: number | undefined,
  threshold = LARGE_COUNT_THRESHOLD
) => {
  const count = selectedFiltered ? rowCountFiltered : rowCountAll;
  return count !== undefined && count > threshold;
};
